import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popover from '@mui/material/Popover';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  checkboxOptionSx,
  checkboxSx,
  ClearAllButton,
  CountBadge,
  DefaultChevron,
  DropdownPaper,
  EmptyText,
  HiddenCheckbox,
  InlineClearAllButton,
  MiniCheckbox,
  OptionRow,
  OptionsList,
  PillChevron,
  popoverSx,
  SearchWrapper,
  searchInputSx,
  SelectAllLabel,
  SelectAllRow,
  ShowHeader,
  showHeaderInnerSx,
  ShowLabel,
  ShowOption,
  showOptionsBoxSx,
  ShowSection,
  showFilterLabelStyle,
  TriggerButton,
  TruncatedText,
} from './SearchableMutliSelect.styles';

type ShowFilter = 'active' | 'archived' | 'all';

export interface SearchableOption {
  id: string;
  name: string;
  isArchived?: boolean;
  status?: string;
}

interface SearchableMultiSelectProps {
  label: string;
  options: SearchableOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  /** 'isArchived' for projects/clients, 'status' for team members */
  statusField?: 'isArchived' | 'status';
  /** Renders as a full-width outlined form field (for use inside forms/dialogs) */
  bordered?: boolean;
  /** Renders as a compact tinted pill, matching the other controls in a filter bar */
  pill?: boolean;
  /**
   * When false, omits the "Select all" checkbox row and instead shows a "Clear All"
   * button (only once something is selected). Defaults to true.
   */
  showSelectAll?: boolean;
  /**
   * Bump this value (e.g. increment a counter) when the parent's "Apply" action fires.
   * When provided, selected options are moved to the top of the list (sorted A-Z) and
   * unselected options remain below in their original order. Omit to keep the list in
   * its incoming order at all times.
   */
  applySignal?: number;
  /**
   * Called whenever the "Show" section's selection changes (Active / Archived / All).
   * Lets the parent apply this as an independent filter — e.g. "Show: Archived" means
   * show all rows belonging to an archived option, regardless of which checkboxes are
   * selected. Only relevant when `statusField` is set.
   */
  onShowFilterChange?: (filter: 'active' | 'archived' | 'all') => void;
}

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
  statusField,
  bordered = false,
  pill = false,
  showSelectAll = true,
  applySignal,
  onShowFilterChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const [showFilter, setShowFilter] = useState<ShowFilter>('active');
  const [showSectionOpen, setShowSectionOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  // Callers often rebuild `options` as a new array literal on every render (e.g. inline
  // `.map()`), even when its contents haven't changed. A content-based key lets the memo
  // below react to real data changes without also re-triggering on that reference churn,
  // which would otherwise resort the list on every checkbox click instead of only on Apply.
  const optionsKey = options
    .map((o) => `${o.id}:${o.name}:${o.isArchived}:${o.status}`)
    .join('|');

  // Selected options sorted A-Z on top, remaining options below in their original order.
  // Recomputed only when applySignal changes (i.e. the parent's Apply was clicked) or the
  // option data itself changes — never merely because the user toggled a checkbox.
  const orderedOptions = useMemo(() => {
    if (applySignal === undefined) return options;
    const applied = latestValueRef.current;
    if (!applied || applied.length === 0) return options;
    const selectedIds = new Set(applied);
    const selected = options
      .filter((o) => selectedIds.has(o.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    const unselected = options.filter((o) => !selectedIds.has(o.id));
    return [...selected, ...unselected];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey, applySignal]);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleToggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const statusFiltered = statusField
    ? orderedOptions.filter((o) => {
        if (showFilter === 'all') return true;
        if (statusField === 'isArchived') {
          return showFilter === 'archived' ? o.isArchived === true : o.isArchived === false;
        }
        if (statusField === 'status') {
          return showFilter === 'archived' ? o.status === 'Inactive' : o.status === 'Active';
        }
        return true;
      })
    : orderedOptions;

  const filtered = statusFiltered.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase()),
  );

  const allFilteredIds = filtered.map((o) => o.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => value.includes(id));
  const someSelected = allFilteredIds.some((id) => value.includes(id));

  const handleSelectAll = () => {
    if (allSelected) {
      onChange(value.filter((id) => !allFilteredIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...value, ...allFilteredIds]));
      onChange(merged);
    }
  };

  const showOptions: Array<{ key: ShowFilter; label: string }> =
    statusField === 'status'
      ? [
          { key: 'all', label: 'Active & Inactive' },
          { key: 'active', label: 'Active' },
          { key: 'archived', label: 'Inactive' },
        ]
      : [
          { key: 'all', label: 'Active & Archived' },
          { key: 'active', label: 'Active' },
          { key: 'archived', label: 'Archived' },
        ];

  // For bordered (form) mode: measure how many names fit before overflowing
  const namesContainerRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const selectedNames = useMemo(
    () => (!pill && value.length > 0 ? options.filter((o) => value.includes(o.id)).map((o) => o.name) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pill, value.join(','), optionsKey],
  );

  const measureNames = () => {
    if (pill || selectedNames.length === 0) { setVisibleCount(null); return; }
    const container = namesContainerRef.current;
    if (!container) return;

    const BADGE_WIDTH = 34;
    const availableWidth = container.offsetWidth - BADGE_WIDTH;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) { setVisibleCount(selectedNames.length); return; }
    const style = window.getComputedStyle(container);
    ctx.font = `${style.fontSize} ${style.fontFamily}`;

    let used = 0;
    let count = 0;
    for (let i = 0; i < selectedNames.length; i++) {
      const text = i === 0 ? selectedNames[i] : `, ${selectedNames[i]}`;
      const w = ctx.measureText(text).width;
      if (count > 0 && used + w > availableWidth) break;
      used += w;
      count++;
    }
    setVisibleCount(Math.max(1, count));
  };

  useLayoutEffect(() => {
    measureNames();
    const container = namesContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(measureNames);
    ro.observe(container);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pill, selectedNames]);

  const displayLabel = label;

  return (
    <>
      <TriggerButton onClick={handleOpen} bordered={bordered} pill={pill} active={pill && value.length > 0}>
        {bordered && selectedNames.length > 0 ? (
          <span
            ref={namesContainerRef}
            style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            <TruncatedText style={{ flexShrink: 1, minWidth: 0 }}>
              {visibleCount === null
                ? selectedNames.join(', ')
                : selectedNames.slice(0, visibleCount).join(', ')}
            </TruncatedText>
            {visibleCount !== null && visibleCount < selectedNames.length && (
              <CountBadge style={{ flexShrink: 0, padding: '0 5px', height: 16, lineHeight: '16px', fontSize: '0.7rem' }}>
                +{selectedNames.length - visibleCount}
              </CountBadge>
            )}
          </span>
        ) : (
          <TruncatedText style={{ opacity: !pill && value.length === 0 ? 0.45 : 1 }}>{displayLabel}</TruncatedText>
        )}
        {pill && value.length > 0 && <CountBadge>{value.length}</CountBadge>}
        {pill ? <PillChevron /> : <DefaultChevron />}
      </TriggerButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={popoverSx}
        PaperProps={{
          elevation: 4,
          sx: {
            overflow: 'hidden',
            mt: pill ? '6px' : 0.5,
            borderRadius: pill ? '14px' : 1.5,
          },
        }}
      >
        <DropdownPaper pill={pill}>
          {/* Search */}
          <SearchWrapper pill={pill}>
            <OutlinedInput
              inputRef={searchRef}
              size="small"
              fullWidth
              placeholder={`Search ${label.toLowerCase()}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
              sx={searchInputSx}
            />
          </SearchWrapper>

          {/* SHOW filter section */}
          {statusField && (
            <>
              <Divider />
              <ShowSection>
                <ShowHeader onClick={() => setShowSectionOpen((p) => !p)}>
                  <ShowLabel>Show</ShowLabel>
                  <Box sx={showHeaderInnerSx}>
                    <span style={showFilterLabelStyle}>
                      {showOptions.find((o) => o.key === showFilter)?.label}
                    </span>
                    {showSectionOpen ? (
                      <KeyboardArrowUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    ) : (
                      <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                  </Box>
                </ShowHeader>
                {showSectionOpen && (
                  <Box sx={showOptionsBoxSx}>
                    {showOptions.map((opt) => (
                      <ShowOption
                        key={opt.key}
                        selected={showFilter === opt.key}
                        onClick={() => {
                          setShowFilter(opt.key);
                          onShowFilterChange?.(opt.key);
                        }}
                      >
                        {opt.label}
                      </ShowOption>
                    ))}
                  </Box>
                )}
              </ShowSection>
              <Divider />
            </>
          )}

          {/* "Clear All" sits above the scroll area so it stays visible;
              "Select all" remains inside the list for non-pill callers. */}
          {!showSelectAll && value.length > 0 && (
            <ClearAllButton type="button" onClick={() => onChange([])}>
              <CloseIcon sx={{ fontSize: 12 }} />
              Clear All
            </ClearAllButton>
          )}

          {/* Options list */}
          <OptionsList pill={pill}>
            {showSelectAll && (
              <SelectAllRow pill={pill}>
                <SelectAllLabel pill={pill} onClick={handleSelectAll}>
                  {pill ? (
                    <MiniCheckbox isSelected={allSelected || someSelected}>
                      {allSelected ? '✓' : someSelected ? '–' : ''}
                    </MiniCheckbox>
                  ) : (
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      onChange={handleSelectAll}
                      sx={checkboxSx}
                    />
                  )}
                  Select all
                </SelectAllLabel>
                {value.length > 0 && (
                  <InlineClearAllButton type="button" onClick={() => onChange([])}>
                    Clear All
                  </InlineClearAllButton>
                )}
              </SelectAllRow>
            )}
            {filtered.length === 0 ? (
              <EmptyText>No results</EmptyText>
            ) : (
              filtered.map((option) => {
                const isSelected = value.includes(option.id);
                return (
                  <OptionRow key={option.id} isSelected={isSelected} pill={pill}>
                    {pill ? (
                      <>
                        <HiddenCheckbox
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggle(option.id)}
                        />
                        <MiniCheckbox isSelected={isSelected} aria-hidden>
                          {isSelected ? '✓' : ''}
                        </MiniCheckbox>
                      </>
                    ) : (
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => handleToggle(option.id)}
                        sx={checkboxOptionSx}
                      />
                    )}
                    <TruncatedText>{option.name}</TruncatedText>
                  </OptionRow>
                );
              })
            )}
          </OptionsList>
        </DropdownPaper>
      </Popover>
    </>
  );
};
